import React, { memo } from 'react';
import { Box, Stack } from 'investira.react.components';

import {
  BenchmarkCard,
  FundoInfoCard,
  RendaFixaInfoCard,
  RetornoCard,
  SharpeCard
} from '../../../../components/molecules';
import AtivoHighlightColumn from './_components/AtivoHighlightColumn';
import {
  AtivoHighlightRetornoTooltip,
  AtivoHighlightSharpeTooltip
} from './_components/AtivoHighlightTooltips';
import {
  ATIVO_HIGHLIGHT_GROUP,
  getAtivoHighlightColumns,
  getAtivoHighlightData,
  normalizeAtivoHighlightSharpe
} from './ativoHighlight.helpers';

const wIndicadorLabelProps = {
  variant: 'caption'
};

const wIndicadorValueProps = {
  variant: 'h6',
  sx: {
    fontWeight: 500
  }
};

/**
 * Apresenta os principais dados de avaliação e acesso aplicáveis ao ativo.
 *
 * @param {Object} pProps Propriedades do componente.
 * @param {Object} pProps.ativo Dados cadastrais do ativo.
 * @param {Object} pProps.ativo_avaliacao Dados de avaliação do ativo.
 * @param {Object} pProps.cotacao Dados normalizados de cotação do ativo.
 * @param {string} pProps.displayMode Família de apresentação do ativo.
 * @returns {JSX.Element|null}
 */
const AtivoHighlight = memo(pProps => {
  const { ativo, ativo_avaliacao, cotacao, displayMode } = pProps;
  const {
    avaliacao: xAvaliacao,
    fundoInfoData: xFundoInfoData,
    hasBenchmarkData: xHasBenchmarkData,
    hasFundoInfo: xHasFundoInfo,
    hasIndicadores: xHasIndicadores,
    hasRendaFixaInfo: xHasRendaFixaInfo,
    hasRetorno: xHasRetorno,
    hasSharpe: xHasSharpe,
    retornoValue: xRetornoValue,
    sharpeValue: xSharpeValue,
    visibleGroups: xVisibleGroups
  } = getAtivoHighlightData(ativo, ativo_avaliacao, displayMode);
  const xColumns = getAtivoHighlightColumns(xVisibleGroups);

  if (xVisibleGroups.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          md: 'repeat(6, minmax(0, 1fr))',
          lg: 'repeat(12, minmax(0, 1fr))'
        },
        alignItems: 'stretch',
        gap: 1.5
      }}>
      {xHasIndicadores && (
        <AtivoHighlightColumn columns={xColumns[ATIVO_HIGHLIGHT_GROUP.indicadores]}>
          <Stack spacing={1.5} flex={1} height="100%">
            {xHasRetorno && (
              <RetornoCard
                label="Retorno em 12 meses"
                value={xRetornoValue}
                labelProps={wIndicadorLabelProps}
                valueProps={wIndicadorValueProps}
                tooltip={<AtivoHighlightRetornoTooltip />}
                tooltipPosition="value"
              />
            )}
            {xHasSharpe && (
              <SharpeCard
                label="Sharpe em 12 meses"
                value={normalizeAtivoHighlightSharpe(xSharpeValue)}
                labelProps={wIndicadorLabelProps}
                valueProps={wIndicadorValueProps}
                tooltip={<AtivoHighlightSharpeTooltip />}
                tooltipPosition="value"
              />
            )}
          </Stack>
        </AtivoHighlightColumn>
      )}
      {xHasBenchmarkData && (
        <AtivoHighlightColumn columns={xColumns[ATIVO_HIGHLIGHT_GROUP.benchmark]}>
          <BenchmarkCard data={xAvaliacao} />
        </AtivoHighlightColumn>
      )}
      {xHasFundoInfo && (
        <AtivoHighlightColumn columns={xColumns[ATIVO_HIGHLIGHT_GROUP.fundoInfo]}>
          <FundoInfoCard data={xFundoInfoData} />
        </AtivoHighlightColumn>
      )}
      {xHasRendaFixaInfo && (
        <AtivoHighlightColumn columns={xColumns[ATIVO_HIGHLIGHT_GROUP.rendaFixaInfo]}>
          <RendaFixaInfoCard cotacao={cotacao} data={ativo} />
        </AtivoHighlightColumn>
      )}
    </Box>
  );
});

AtivoHighlight.displayName = 'AtivoHighlight';

export default AtivoHighlight;
